/**
 * GÜNLÜK ANALİZ — hesap karşılaştırması ve ölçüler (back-test, 14.10.2026).
 *
 * KARŞILAŞTIRMA: iki ya da daha çok hesap (gerçek ya da sanal) seçilince her
 * hesap kendi çizgisi; tek bir alan (varsayılan: geçmiş dahil getiri %).
 * Renk hesabın TANIMA rengi (yargı değil); rengi olmayana sıradaki ton.
 *
 * ÖLÇÜLER: her seri için motorun `olculer`i (zincirlenmiş getiri, en büyük
 * düşüş, en iyi/kötü yıl ve ay, oynaklık, kıyas). Arayüz hesap yapmaz;
 * yalnızca motorun metinlerini gösterir. Kıyas serisinin fiyatı önce
 * eşitlenir (yalnızca kimlik + tarih aralığı gider).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { IsoDate } from "@parafon/contracts";
import type { Absence, Olculer, SeriesField, ValuationSeriesPoint } from "@parafon/contracts/local-api";
import { useCentral } from "../central/CentralContext";
import { araclariEsitle } from "../lib/aracEsitle";
import { formatMoney, formatRatio } from "../lib/decimal";
import { formatIsoDate } from "../lib/format";
import type { KapsamSecenegi } from "../lib/hesapKapsami";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { CizgiGrafik } from "../screens/GrafikScreen";
import { Field, Select, cx } from "../ui/primitives";
import { M, YEREL } from "../dil";
import { errorText } from "../lib/rpcError";
import { motorMetni, motorMetniVeya } from "../dil/motorMetni";
import { eksenTutar } from "../lib/tutarGizleme";

type Seri = { readonly id: string | null; readonly points: readonly ValuationSeriesPoint[] };

const YEDEK_RENKLER = [
  "var(--pf-hesap-mavi)",
  "var(--pf-hesap-eflatun)",
  "var(--pf-hesap-turkuaz)",
  "var(--pf-hesap-kahverengi)",
  "var(--pf-hesap-mor)",
];

export function seriAdi(id: string | null, secenekler: readonly KapsamSecenegi[]): string {
  if (id === null) return M.grafik.kars.butunKasa;
  const s = secenekler.find((x) => x.id === id);
  // "temettü hariç" hesap adının yanında da (kullanıcı kararı 19.10.2026).
  return s === undefined
    ? id
    : s.sanal
      ? s.temettuHaric === true
        ? M.grafik.kars.sanalTemettuHaric(s.ad)
        : M.grafik.kars.sanal(s.ad)
      : s.ad;
}

const ALANLAR: readonly { readonly alan: SeriesField; readonly ad: string; readonly oran: boolean }[] = [
  { alan: "totalReturnRatio", ad: M.grafik.alan.totalReturnRatio, oran: true },
  { alan: "totalReturn", ad: M.grafik.alan.totalReturn, oran: false },
  { alan: "value", ad: M.grafik.alan.value, oran: false },
];

interface KNokta {
  readonly date: IsoDate;
  readonly n: Readonly<Record<string, ValuationSeriesPoint | undefined>>;
}

export function Karsilastirma({
  seriler,
  secenekler,
  currency,
}: {
  readonly seriler: readonly Seri[];
  readonly secenekler: readonly KapsamSecenegi[];
  readonly currency: string;
}) {
  const [alan, setAlan] = useState<SeriesField>("totalReturnRatio");
  const [kapali, setKapali] = useState<ReadonlySet<string>>(new Set());
  const [secili, setSecili] = useState<number | null>(null);
  const tanim = ALANLAR.find((a) => a.alan === alan) ?? ALANLAR[0];

  const noktalar = useMemo<KNokta[]>(() => {
    const gunler = new Set<IsoDate>();
    for (const s of seriler) for (const p of s.points) gunler.add(p.date);
    const haritalar = seriler.map((s) => new Map(s.points.map((p) => [p.date, p])));
    return [...gunler].sort().map((date) => ({
      date,
      n: Object.fromEntries(seriler.map((s, i) => [s.id ?? "", haritalar[i]?.get(date)])),
    }));
  }, [seriler]);

  const cizgiler = seriler.map((s, i) => {
    const sec = secenekler.find((x) => x.id === s.id);
    const renk = sec?.renk !== undefined ? `var(--pf-hesap-${sec.renk})` : (YEDEK_RENKLER[i % YEDEK_RENKLER.length] as string);
    return { alan: s.id ?? "", ad: seriAdi(s.id, secenekler), renk: { tur: "sabit" as const, renk }, kesikli: sec?.sanal === true };
  });
  const sec = secili !== null && secili < noktalar.length ? secili : noktalar.length - 1;
  const deger = (p: KNokta, id: string): string | null => p.n[id]?.[alan] ?? null;
  const metin = (v: string | null) =>
    v === null ? "—" : tanim?.oran === true ? formatRatio(v) : `${formatMoney(v)} ${currency}`;

  return (
    <div className="space-y-2">
      <Field label={M.grafik.kars.karsilastirilan} htmlFor="karsilastir-alan" className="w-64">
        <Select
          id="karsilastir-alan"
          value={alan}
          options={ALANLAR.map((a) => ({ value: a.alan, label: a.ad }))}
          onChange={(e) => setAlan(e.currentTarget.value as SeriesField)}
        />
      </Field>
      <CizgiGrafik<KNokta>
        baslik={M.grafik.kars.baslik(tanim?.ad ?? "")}
        cizgiler={cizgiler}
        kapali={kapali}
        onDegistir={(a) =>
          setKapali((k) => {
            const y = new Set(k);
            if (y.has(a)) y.delete(a);
            else y.add(a);
            return y;
          })
        }
        points={noktalar}
        secili={sec}
        onSec={setSecili}
        eksen={(n) =>
          tanim?.oran === true
            ? M.bicim.yuzde((n * 100).toLocaleString(YEREL, { maximumFractionDigits: 1 }))
            : eksenTutar(n)
        }
        sifir
        deger={deger}
        balon={(p, x, sagda) => (
          <div
            role="tooltip"
            className="pointer-events-none absolute top-2 z-10 w-max max-w-[20rem] rounded-[var(--pf-radius-sm)] border border-line bg-surface p-2 text-[11px] shadow-md"
            style={sagda ? { left: x + 12 } : { right: `calc(100% - ${x - 12}px)` }}
          >
            <p className="font-semibold text-ink">{formatIsoDate(p.date)}</p>
            <table className="mt-1">
              <tbody>
                {cizgiler.map((c) => (
                  <tr key={c.alan}>
                    <th scope="row" className="pr-3 text-left font-normal text-muted">
                      {c.ad}
                    </th>
                    <td className="pf-num text-right text-ink">{metin(deger(p, c.alan))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      />
      {noktalar[sec] !== undefined && (
        <section aria-label={M.grafik.seciliGun} className="pf-card p-3 text-[12px] pointer-fine:hidden">
          <p className="font-semibold">{formatIsoDate((noktalar[sec] as KNokta).date)}</p>
          <ul>
            {cizgiler.map((c) => (
              <li key={c.alan}>
                {c.ad}: <span className="pf-num">{metin(deger(noktalar[sec] as KNokta, c.alan))}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ölçüler
// ---------------------------------------------------------------------------

export const KIYAS_SECENEKLERI: readonly { readonly value: string; readonly label: string }[] = [
  { value: "", label: M.grafik.olcu.kiyasYok },
  { value: "bist:XU100", label: "BIST 100" },
  { value: "comex:GC=F", label: M.grafik.olcu.altin },
  { value: "kur:USD", label: M.grafik.olcu.dolar },
  { value: "kur:EUR", label: M.grafik.olcu.euro },
];

function Yok({ a }: { readonly a: Absence | undefined }) {
  const olculemedi = a === undefined || a.kind === "olculemedi";
  return (
    <span className={olculemedi ? "text-warn" : "text-faint"} title={motorMetniVeya(a?.reason, M.grafik.olcu.motorSebep)}>
      —
    </span>
  );
}

export function OlculerPaneli({
  seriler,
  secenekler,
  currency,
  from,
  to,
}: {
  readonly seriler: readonly Seri[];
  readonly secenekler: readonly KapsamSecenegi[];
  readonly currency: string;
  readonly from: IsoDate;
  readonly to: IsoDate;
}) {
  const { repo } = useRepositoryContext();
  const merkez = useCentral();
  const merkezRef = useRef(merkez);
  merkezRef.current = merkez;
  const [kiyas, setKiyas] = useState("");
  const [sonuc, setSonuc] = useState<
    { readonly durum: "hesaplaniyor" } | { readonly durum: "tamam"; readonly olculer: readonly (Olculer | null)[] } | { readonly durum: "hata"; readonly hata: unknown }
  >({ durum: "hesaplaniyor" });

  useEffect(() => {
    let iptal = false;
    const olc = repo.olculer;
    if (olc === undefined) {
      setSonuc({ durum: "tamam", olculer: seriler.map(() => null) });
      return undefined;
    }
    setSonuc({ durum: "hesaplaniyor" });
    void (async () => {
      if (kiyas !== "") {
        await araclariEsitle(repo, merkezRef.current, { instrumentIds: [kiyas], from, to, paralar: [currency] });
      }
      const out: (Olculer | null)[] = [];
      for (const s of seriler) {
        out.push(await olc({ noktalar: s.points, reportCurrency: currency, ...(kiyas === "" ? {} : { kiyas }) }));
      }
      if (!iptal) setSonuc({ durum: "tamam", olculer: out });
    })().catch((hata: unknown) => {
      if (!iptal) setSonuc({ durum: "hata", hata });
    });
    return () => {
      iptal = true;
    };
  }, [repo, seriler, kiyas, currency, from, to]);

  const oran = (v: string | null | undefined, a: Absence | undefined) =>
    v == null ? <Yok a={a} /> : <span className="pf-tek">{formatRatio(v)}</span>;
  const donem = (d: Olculer["enIyiYil"], a: Absence | undefined) =>
    d === null ? (
      <Yok a={a} />
    ) : (
      <>
        <span className="pf-tek">{formatRatio(d.oran)}</span> <span className="text-faint">{d.donem.length === 7 ? `${d.donem.slice(5)}.${d.donem.slice(0, 4)}` : d.donem}{d.tam ? "" : M.grafik.olcu.kismi}</span>
      </>
    );

  return (
    <section aria-label={M.grafik.olcu.baslik} className="pf-card space-y-2 p-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-[13px] font-semibold text-ink">{M.grafik.olcu.baslik}</h2>
          <p className="text-[11px] text-muted">{M.grafik.olcu.aciklama}</p>
        </div>
        <Field label={M.grafik.olcu.kiyas} htmlFor="olcu-kiyas" className="w-44">
          <Select id="olcu-kiyas" value={kiyas} options={[...KIYAS_SECENEKLERI]} onChange={(e) => setKiyas(e.currentTarget.value)} />
        </Field>
      </div>
      {sonuc.durum === "hesaplaniyor" && <p className="text-[12px] text-muted">{M.grafik.olcu.hesaplaniyor}</p>}
      {sonuc.durum === "hata" && (
        <p className="text-[12px] text-warn">
          {M.grafik.olcu.hata(errorText(sonuc.hata))}
        </p>
      )}
      {sonuc.durum === "tamam" && (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="text-left text-muted">
              <tr>
                <th className="py-1 pr-3">{M.grafik.olcu.hesap}</th>
                <th className="py-1 pr-3 text-right">{M.grafik.olcu.getiri}</th>
                <th className="py-1 pr-3 text-right">{M.grafik.olcu.enBuyukDusus}</th>
                <th className="py-1 pr-3 text-right">{M.grafik.olcu.enIyiYil}</th>
                <th className="py-1 pr-3 text-right">{M.grafik.olcu.enKotuYil}</th>
                <th className="py-1 pr-3 text-right">{M.grafik.olcu.enIyiAy}</th>
                <th className="py-1 pr-3 text-right">{M.grafik.olcu.enKotuAy}</th>
                <th className="py-1 pr-3 text-right">{M.grafik.olcu.oynaklik}</th>
                {kiyas !== "" && <th className="py-1 pr-3 text-right">{M.grafik.olcu.kiyasaFark}</th>}
              </tr>
            </thead>
            <tbody>
              {seriler.map((s, i) => {
                const o = sonuc.olculer[i] ?? null;
                return (
                  <tr key={s.id ?? "defter"} className="border-t border-line align-top">
                    <th scope="row" className="py-1 pr-3 text-left font-normal">
                      {seriAdi(s.id, secenekler)}
                      {o?.not != null && <span className="block text-[11px] text-warn">{motorMetni(o.not)}</span>}
                    </th>
                    {o === null ? (
                      <td colSpan={8} className="py-1 text-faint">{M.grafik.olcu.derlemedeYok}</td>
                    ) : (
                      <>
                        <td className="pf-num py-1 pr-3 text-right">{oran(o.getiri, o.absent.getiri)}</td>
                        <td className="pf-num py-1 pr-3 text-right">
                          {o.enBuyukDusus === null ? (
                            <Yok a={o.absent.enBuyukDusus} />
                          ) : (
                            <>
                              {formatRatio(o.enBuyukDusus.oran)}
                              <span className="block whitespace-normal text-[11px] text-faint">
                                {formatIsoDate(o.enBuyukDusus.zirve)} → {formatIsoDate(o.enBuyukDusus.dip)};{" "}
                                {o.enBuyukDusus.toparlanma === null
                                  ? M.grafik.olcu.toparlanmadi
                                  : M.grafik.olcu.toparlandi(formatIsoDate(o.enBuyukDusus.toparlanma))}
                              </span>
                            </>
                          )}
                        </td>
                        <td className="pf-num py-1 pr-3 text-right">{donem(o.enIyiYil, o.absent.enIyiYil)}</td>
                        <td className="pf-num py-1 pr-3 text-right">{donem(o.enKotuYil, o.absent.enKotuYil)}</td>
                        <td className="pf-num py-1 pr-3 text-right">{donem(o.enIyiAy, o.absent.enIyiAy)}</td>
                        <td className="pf-num py-1 pr-3 text-right">{donem(o.enKotuAy, o.absent.enKotuAy)}</td>
                        <td className="pf-num py-1 pr-3 text-right">{oran(o.oynaklik, o.absent.oynaklik)}</td>
                        {kiyas !== "" && (
                          <td className={cx("pf-num py-1 pr-3 text-right")}>
                            {o.kiyas === null ? (
                              <Yok a={o.absent.kiyas} />
                            ) : (
                              <>
                                {formatRatio(o.kiyas.fark)}
                                <span className="block text-[11px] text-faint">
                                  {M.grafik.olcu.kiyasEtiket} <span className="pf-tek">{formatRatio(o.kiyas.getiri)}</span>
                                </span>
                              </>
                            )}
                          </td>
                        )}
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
